/**
 * Domain vocabulary shared by the console server and the web client.
 *
 * Every enum is declared once as a readonly tuple so that it can feed
 * the database schema, the API validation layer and the UI labels.
 */
export const PROJECT_STATUSES = ['idea', 'development', 'testing', 'production', 'maintenance', 'archived'];
/** Technical health. `unknown` is the default whenever no recent check exists. */
export const HEALTH_STATES = ['operational', 'degraded', 'down', 'unknown'];
export const INTEGRATION_STATES = ['draft', 'incomplete', 'validated', 'disconnected'];
export const CRITICALITIES = ['low', 'medium', 'high', 'critical'];
export const ENVIRONMENT_KINDS = ['development', 'staging', 'production', 'other'];
export const COMPONENT_KINDS = ['service', 'database', 'storage', 'queue', 'other'];
export const ENVIRONMENT_LINK_KINDS = ['app', 'admin', 'repository', 'hosting', 'logs', 'pipeline', 'tracking', 'other'];
/** Capabilities a connector may declare (F06). The UI only enables declared ones. */
export const CONNECTOR_CAPABILITIES = [
    'inventory',
    'source',
    'health',
    'backup',
    'restore',
    'storage',
    'deploy',
    'logs',
    'costs',
    'notify',
    'dns',
    'security',
    'pipelines',
    'errors',
];
export const CONNECTION_STATUSES = ['untested', 'connected', 'error', 'revoked'];
export const BACKUP_STATUSES = [
    'scheduled',
    'pending',
    'running',
    'succeeded',
    'partial',
    'failed',
    'cancelled',
    'expired',
];
export const RESTORE_STATUSES = [
    'draft',
    'prechecked',
    'queued',
    'running',
    'succeeded',
    'failed',
    'interrupted',
    'cancelled',
];
/**
 * Lifecycle of a long-running operation.
 * `interrupted` means the worker vanished: the real state must be diagnosed
 * before any new attempt. It is never converted into a success.
 */
export const OPERATION_STATUSES = ['queued', 'running', 'succeeded', 'partial', 'failed', 'cancelled', 'interrupted'];
export const ACTIVE_OPERATION_STATUSES = ['queued', 'running', 'interrupted'];
export const OPERATION_TYPES = ['backup', 'restore', 'stack_analysis', 'changelog_sync', 'connection_test', 'retention', 'console_backup', 'deploy', 'security_scan', 'error_sync', 'env_inventory'];
export const ALERT_SEVERITIES = ['info', 'warning', 'critical'];
export const ALERT_STATUSES = ['open', 'acknowledged', 'resolved'];
export const ALERT_TYPES = [
    'monitor_down',
    'certificate_expiring',
    'backup_failed',
    'backup_partial',
    'backup_deadline_missed',
    'restore_failed',
    'restore_interrupted',
    'restore_test_failed',
    'deployment_non_compliant',
    'deployment_failed',
    'domain_error',
    'docs_conflict',
    'changelog_invalid',
    'connection_error',
    'worker_silent',
    'security_finding',
    'security_review_due',
    'error_new',
    'error_regression',
    'incident_opened',
    'env_variable_missing',
];
export const DEPLOYMENT_SOURCES = ['controlled', 'external'];
export const VERSIONING_SCOPES = ['global', 'per_service'];
export const AUDIT_RESULTS = ['success', 'denied', 'failure'];
/** Data freshness for any collected value (F09 / principles §5). */
export const FRESHNESS_STATES = ['fresh', 'stale', 'never'];
/** Onboarding wizard steps (F02), in order. */
export const ONBOARDING_STEPS = [
    'project',
    'repositories',
    'stack',
    'environments',
    'services',
    'changelog',
    'backup',
    'tests',
    'validation',
];
/** F11 — security findings aggregated from connected tools and console checks. */
export const FINDING_CATEGORIES = ['dependency', 'secret', 'code', 'certificate', 'access', 'configuration'];
export const FINDING_SEVERITIES = ['low', 'medium', 'high', 'critical'];
/** `accepted` and `false_positive` require a justification; `accepted` also a review date. */
export const FINDING_STATUSES = ['open', 'in_progress', 'fixed', 'false_positive', 'accepted'];
/** Synthetic posture. `unknown` whenever a check is missing, stale or disconnected: never "secure" by default. */
export const SECURITY_POSTURES = ['secure', 'at_risk', 'critical', 'unknown'];
/** F10 — lifecycle shared by error groups and incidents. */
export const ISSUE_STATUSES = ['new', 'analyzing', 'in_progress', 'resolved', 'verified'];
export const ERROR_LEVELS = ['debug', 'info', 'warning', 'error', 'fatal'];
export const INCIDENT_SEVERITIES = ['minor', 'major', 'critical'];
export const INCIDENT_EVENT_KINDS = ['note', 'action', 'status', 'link', 'detection'];
//# sourceMappingURL=domain.js.map